import _ from "lodash";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { Formik, Form, useFormikContext } from "formik";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import { DateTime } from "luxon";
import { ReactSelectField } from "../inputGroup/helpers";
import { Price } from "../../common/Price";
import { IntegersField } from "../inputGroup/helpers/numbers";
import { InlineIcon } from "@iconify/react";
import { Placeholder } from "rsuite";
import ReactGA4 from "react-ga4";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "../../common/Button";
import jssVariables, { buttonStylesType1 } from "../../../jss_setup";
import { modalGenericStyles } from "../../../jss_setup/modals";
import update from "immutability-helper";
import { NoScrollContext } from "../../../context";

const SlotField = ({ id }) => {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "ActSchedulesModal",
  });

  const group = _.get(values, "group.value");
  useEffect(() => {
    if (!_.get(values, "group.value")) {
      return;
    }

    setFieldValue("slot", null);
  }, [group]);

  return (
    <ReactSelectField
      id={id}
      name="slot"
      label={t("slot")}
      options={[
        ..._.get(values, "group.slots", []).map((slot) => ({
          ...slot,
          value: slot.time.substring(0, slot.time.length - 3),
          label: slot.time.substring(0, slot.time.length - 3),
        })),
      ]}
      isMulti={false}
      placeholder={"Select option"}
      customStyles={customStyles}
    />
  );
};
SlotField.propTypes = {
  id: PropTypes.string,
};

const productsFieldStyles = createUseStyles({
  singleOptionContainer: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.singleOptionContainer", {}),
  }),
  priceContr: (props) => ({
    fontSize: "medium",
    ..._.get(props, "customStyles.priceContr", {}),
  }),
  freePrice: (props) => ({
    color: jssVariables.colors.base_color,
    fontWeight: "bold",
    textTransform: "capitalize",
    fontSize: "x-large",
    paddingLeft: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.freePrice", {}),
  }),
});
const ProductsField = ({ products }) => {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useContext(TranslationContext);

  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "ActSchedulesModal",
  });

  const classes = productsFieldStyles({ customStyles });

  const slot = _.get(values, "slot");
  useEffect(() => {
    if (!slot) {
      return;
    }

    setFieldValue(
      "products",
      _.get(slot, "products", []).map((pr) => ({ ...pr, quantity: 0 }))
    );
  }, [slot]);

  return products.map((product, idx) => (
    <span className={classes.singleOptionContainer} key={idx}>
      <IntegersField
        name={`products.${idx}.quantity`}
        label={`${t("selectYourTickets")}: ${t(product.name)}`}
        placeholder={"Select option"}
        title={
          product.price.value > 0 ? (
            <div className={classes.priceContr}>
              <Price
                price={product.price.value}
                currency={product.price.currency}
                styleless
              />
            </div>
          ) : (
            <span className={classes.freePrice}>{t("free")}</span>
          )
        }
        minValue={product.min_pax}
        maxValue={product.max_pax}
        messageWhenMin={t("clickResetBtn")}
        customStyles={customStyles}
      />
    </span>
  ));
};
ProductsField.propTypes = {
  products: PropTypes.array.isRequired,
};

const LangField = () => {
  const { values, setFieldValue } = useFormikContext();
  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "ActSchedulesModal",
  });

  const slot = _.get(values, "slot");
  useEffect(() => {
    if (!slot) {
      return;
    }

    const lang = _.get(slot, "products.0.languages.0", {
      name: "English",
      code: "en",
    });
    setFieldValue("language", { label: lang.name, value: lang.code });
  }, [slot]);

  return (
    <ReactSelectField
      name="language"
      label={t("language")}
      options={_.get(slot, "products.0.languages", []).map((lng) => ({
        value: lng.code,
        label: lng.name,
      }))}
      isMulti={false}
      placeholder={"Select option"}
      customStyles={{
        customStyles: { ...customStyles, label: { fontWeight: "normal" } },
      }}
    />
  );
};

const schedulesModalStyles = createUseStyles({
  ...modalGenericStyles,
  underlay: (props) => ({
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100vh",
    background: "rgba(0,0,0, .5)",
    backdropFilter: "blur(5px)",
    zIndex: 9999,
    display: "grid",
    justifyItems: "center",
    paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
    ..._.get(props, "customStyles.underlay", {}),
  }),
  schedulesModal: (props) => ({
    width: "90vw",
    height: "max-content",
    overflow: props.hasOverflow ? "auto" : "unset",
    maxHeight: "80vh",
    borderRadius: jssVariables.border_radius,
    boxShadow: jssVariables.shadows.normalShadow,
    background: "white",
    padding: `calc(${jssVariables.normal_gap} * 2)`,
    zIndex: 9999,
    display: "grid",
    alignContent: "baseline",
    ..._.get(props, "customStyles.schedulesModal", {}),
  }),
  header: (props) => ({
    fontSize: "x-large",
    fontWeight: "bold",
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    alignItems: "center",
    justifyContent: "space-between",
    ..._.get(props, "customStyles.header", {}),
  }),
  placeholderHeader: (props) => ({
    marginBottom: "2.5rem",
    ..._.get(props, "customStyles.placeholderHeader", {}),
  }),
  closeIcon: (props) => ({
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  singleOptionContainer: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    ..._.get(props, "customStyles.singleOptionContainer", {}),
  }),
  singleOption: (props) => ({
    fontSize: "medium",
    border: `1px solid ${jssVariables.colors.base_color}`,
    borderRadius: "5px",
    background: "white",
    padding: `calc(${jssVariables.normal_gap} * 1)`,
    height: "3rem",
    ..._.get(props, "customStyles.singleOption", {}),
  }),
  date: (props) => ({
    fontSize: "large",
    padding: `${jssVariables.normal_gap} 0`,
    ..._.get(props, "customStyles.date", {}),
  }),
  inputGroup: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.inputGroup", {}),
  }),
  containers: (props) => ({
    display: "grid",
    ..._.get(props, "customStyles.containers", {}),
  }),
  infoContainer: (props) => ({
    display: "grid",
    alignContent: "baseline",
    ..._.get(props, "customStyles.infoContainer", {}),
  }),
  infoRow: (props) => ({
    fontSize: "medium",
    fontWeight: "bold",
    ..._.get(props, "customStyles.ticketOptions", {}),
  }),
  bottomContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    fontSize: "large",
    fontWeight: "bold",
    alignItems: "center",
    ..._.get(props, "customStyles.bottomContainer", {}),
  }),
  btmContLabel: (props) => ({
    fontSize: "large",
    fontWeight: "bold",
    paddingRight: jssVariables.normal_gap,
    ..._.get(props, "customStyles.btmContLabel", {}),
  }),
  label: (props) => ({
    fontSize: "medium",
    fontWeight: "normal",
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.label", {}),
  }),
  actions: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(3, max-content)",
    justifyContent: "end",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.actions", {}),
  }),
  btn: (props) => ({
    ...buttonStylesType1,
    ..._.get(props, "customStyles.btn", {}),
  }),
  freePrice: (props) => ({
    color: jssVariables.colors.base_color,
    fontWeight: "bold",
    textTransform: "capitalize",
    fontSize: "x-large",
    paddingLeft: `calc(${jssVariables.normal_gap} / 2)`,
    ..._.get(props, "customStyles.freePrice", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    underlay: (props) => ({
      paddingTop: `calc(${jssVariables.normal_gap} * 2)`,
      ..._.get(props, "customStyles.underlay.smallscreen", {}),
    }),
    schedulesModal: (props) => ({
      width: "30rem",
      height: "max-content",
      maxHeight: "85vh",
      boxShadow: jssVariables.shadows.normalShadow,
      background: "white",
      padding: `calc(${jssVariables.normal_gap} * 2)`,
      zIndex: 9999,
      display: "grid",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      // alignContent: "baseline",
      ..._.get(props, "customStyles.schedulesModal.smallscreen", {}),
    }),
    header: (props) => ({
      fontSize: "xx-large",
      ..._.get(props, "customStyles.header.smallscreen", {}),
    }),
    containers: (props) => ({
      display: "grid",
      gridGap: `calc(${jssVariables.normal_gap} * 3)`,
      ..._.get(props, "customStyles.containers.smallscreen", {}),
    }),
    btmContLabel: (props) => ({
      fontSize: "x-large",
      ..._.get(props, "customStyles.btmContLabel.smallscreen", {}),
    }),
  },
});
export const SchedulesModal = ({
  activity_uid,
  page_session_id,
  showForm,
  bookingData,
  modalLoading,
  pickUpAndDates,
  setModalLoading,
  onSetShowModal,
}) => {
  const [hasOverflow, setHasOverflow] = useState(false);

  const history = useHistory();
  const { toggleNoScroll } = useContext(NoScrollContext);
  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const customStyles = cStyler({ type, brand, objName: "ActSchedulesModal" });
  const classes = schedulesModalStyles({ customStyles, showForm, hasOverflow });
  const groups = bookingData?.schedules?.[0]?.groups ?? [];

  useEffect(() => {
    if (bookingData) setModalLoading(false);
  }, [bookingData, modalLoading]);

  // Make sure to unlock scroll when modal exits
  useEffect(() => () => toggleNoScroll(false), []);

  function onPreBook({ data }) {
    const prebook = window.btoa(JSON.stringify({ uid: activity_uid, ...data }));

    history.push({
      pathname: `/activity_details/booking_process/${page_session_id}/${prebook}`,
    });
    ReactGA4.event({
      category: "action",
      action: "Prebook_activity",
      label: "Prebook_activity",
    });
  }

  return (
    <div className={classes.underlay}>
      <div className={classes.schedulesModal}>
        <span className={classes.header}>
          {t("availabilityAndPrices")}
          <InlineIcon
            icon="bi:x-lg"
            onClick={() => onSetShowModal(false)}
            className={classes.closeIcon}
          />
          {modalLoading ? (
            <Placeholder.Paragraph
              rows={1}
              active
              className={classes.placeholderHeader}
            />
          ) : (
            <span className={classes.date}>
              {DateTime.fromISO(bookingData.date).toLocaleString(
                DateTime.DATE_MED_WITH_WEEKDAY
              )}
            </span>
          )}
        </span>
        {modalLoading ? (
          <React.Fragment>
            <Placeholder.Paragraph rows={2} active />
            <br />
            <br />
            <Placeholder.Paragraph rows={2} active />
          </React.Fragment>
        ) : (
          <Formik
            initialValues={{
              date: DateTime.fromISO(bookingData.date).toISODate(),
              group: null,
              slot: null,
              products: [[0]],
              language: {
                name: "English",
                code: "en",
              },
              pickup: null,
            }}
            validate={(values) => {
              if (values.slot && !hasOverflow) {
                setHasOverflow(true);
              }
            }}
            onSubmit={async (values) => {
              const adultProduct = values.products.some(
                (prod) => prod.guest_type === "ADT" && prod.quantity > 0
              );
              if (adultProduct) {
                const items = values.products
                  .filter((p) => p.quantity > 0)
                  .map((prod) => ({
                    id: prod.product_id,
                    qnt: prod.quantity,
                    pick: pickUpAndDates.pickup_point,
                    name: prod.name,
                    prc: {
                      cur: prod.price.currency,
                      val: prod.price.value,
                    },
                  }));

                onPreBook({
                  data: {
                    items,
                    lng: values.language.value ? values.language.value : "en",
                    date: values.date,
                    optn: values.group.name,
                  },
                });
              } else {
                toast.warning(t("pleaseSelectAdult"), { duration: 6000 });
              }
            }}>
            {({ values, setFieldValue }) => (
              <div className={classes.containers}>
                <Form className={classes.form}>
                  <React.Fragment>
                    <span className={classes.label}>{t("ticketOptions")}</span>
                    <div className={classes.inputGroup}>
                      <ReactSelectField
                        id="actTicketOptions"
                        name="group"
                        options={_.sortBy(
                          groups.map((gr) => ({
                            ...gr,
                            value: gr.name,
                            label: gr.name,
                          })),
                          (v) => v.label
                        )}
                        isMulti={false}
                        placeholder={"Select option"}
                        customStyles={customStyles}
                      />
                      {values.group && <SlotField id="actSlot" />}
                      {values.slot && (
                        <React.Fragment>
                          <ProductsField
                            products={_.get(values, "slot.products", [])}
                          />
                          {_.get(values, "slot.products.0.languages", [])
                            .length === 0 ? (
                            <span className={classes.singleOptionContainer}>
                              <span className={classes.label}>
                                {t("language")}
                              </span>
                              <div className={classes.singleOption}>
                                {t("onlyAvailableInEnglish")}
                              </div>
                            </span>
                          ) : (
                            <LangField />
                          )}
                          {values.products.some(
                            (pr) => pr.guest_type === "ADT" && pr.quantity > 0
                          ) && (
                            <React.Fragment>
                              <div className={classes.bottomContainer}>
                                <span className={classes.btmContLabel}>
                                  {t("total")}:{" "}
                                </span>
                                <Price
                                  price={_.sum(
                                    _.get(values, "products", []).map(
                                      (prod) => prod.price.value * prod.quantity
                                    )
                                  )}
                                  currency={_.get(
                                    values,
                                    "products.0.price.currency"
                                  )}
                                  customStyles={
                                    customStyles.priceComponentTotal
                                  }
                                />
                              </div>
                              <div className={classes.actions}>
                                <Button
                                  text={t("cancel")}
                                  bold={true}
                                  revert={true}
                                  size="small"
                                  onClick={() => onSetShowModal(false)}
                                  customStyles={{
                                    ButtonDefault: {
                                      ..._.get(customStyles, "btnStyles", {}),
                                    },
                                  }}
                                />
                                <Button
                                  text={t("resetTickets")}
                                  bold={true}
                                  revert={true}
                                  size="small"
                                  customStyles={{
                                    ButtonDefault: {
                                      ..._.get(customStyles, "btnStyles", {}),
                                    },
                                  }}
                                  onClick={() =>
                                    setFieldValue(
                                      "products",
                                      update(values.products, {
                                        $apply: (p) =>
                                          p.map((prod) =>
                                            update(prod, {
                                              quantity: { $set: 0 },
                                            })
                                          ),
                                      })
                                    )
                                  }
                                />
                                <Button
                                  id="actNextStep"
                                  text={t("nextStep")}
                                  bold={true}
                                  size="small"
                                  type="submit"
                                  customStyles={{
                                    ButtonDefault: {
                                      ..._.get(customStyles, "btnStyles", {}),
                                    },
                                  }}
                                />
                              </div>
                            </React.Fragment>
                          )}
                        </React.Fragment>
                      )}
                    </div>
                  </React.Fragment>
                </Form>
              </div>
            )}
          </Formik>
        )}
      </div>
    </div>
  );
};
SchedulesModal.defaultProps = {
  page_session_id: "",
  price: 0,
  booking_mode: "RQ",
  versions: [],
  showForm: false,
  activity_uid: "",
  pickUpAndDates: {},
  modalLoading: false,
};
SchedulesModal.propTypes = {
  bookingData: PropTypes.object.isRequired,
  page_session_id: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
  booking_mode: PropTypes.oneOf(["RQ", "CO"]).isRequired,
  reference: PropTypes.string,
  showForm: PropTypes.bool.isRequired,
  activity_uid: PropTypes.string.isRequired,
  pickUpAndDates: PropTypes.object.isRequired,
  modalLoading: PropTypes.bool.isRequired,
  setModalLoading: PropTypes.func.isRequired,
  onSetShowModal: PropTypes.func.isRequired,
};
